import { type Normalised, srcAt } from './normalise.ts';
/**
 * Intake hygiene (ENGINE-SPEC §2.1 N7 and N8, ROADMAP D8): a per-block rewrite of the canonical text, run by `buildDoc`
 * right after `normaliseBlock`, so every channel (including the ones that match regexes or phrases over block text)
 * reads the same clean words.
 *
 *  - N7 (invisible characters): U+200B and U+FEFF are removed everywhere; U+00AD, U+200C, U+200D and U+2060 only
 *    word-internally (the nearest non-N7 code point on each side is a Latin, Greek or Cyrillic letter or a combining
 *    mark). Elsewhere they are typographic (U+2060 beside a dash, a ZWJ in an emoji or Indic sequence) and stay.
 *  - N8 (look-alike letters): Cyrillic letters that look like Latin ones are mapped to Latin, only inside a run that
 *    also has an ASCII Latin letter (rule a) or as an isolated all-Cyrillic run between ASCII-Latin runs (rule b).
 *    Greek is never mapped: human scientific text uses Greek letters as symbols inside Latin words.
 *
 * A removed character is like skipped markup for the source offset map: the map gets a new `(textOffset, srcOffset)`
 * pair after it, and the block's `toNew` (old offset → new offset, used for the emphasis spans) is composed with the
 * removal. A look-alike mapping is 1:1 in UTF-16 length (every mapped letter is in the BMP), so it never touches the map.
 * The whole step is O(n) per block and returns the block untouched, with no allocation, when it has nothing to do.
 * Nothing here is a channel score or a model input; the counts are shown as evidence (`Doc.hygiene`).
 */
import type { HygieneEvent } from './types.ts';

/** N7, removed everywhere in a block. */
export const N7_EVERYWHERE: readonly number[] = [0x200b, 0xfeff];
/** N7, removed only word-internally: soft hyphen, ZWNJ, ZWJ, word joiner. */
export const N7_WORD_INTERNAL: readonly number[] = [0x00ad, 0x200c, 0x200d, 0x2060];

/** N8: Cyrillic code point → Latin letter. Lower case, then upper case. Greek is deliberately absent. */
const CONFUSABLE_PAIRS: ReadonlyArray<readonly [number, string]> = [
	[0x0430, 'a'], // а
	[0x0435, 'e'], // е
	[0x0456, 'i'], // і
	[0x0458, 'j'], // ј
	[0x043e, 'o'], // о
	[0x0440, 'p'], // р
	[0x0441, 'c'], // с
	[0x0445, 'x'], // х
	[0x0443, 'y'], // у
	[0x0455, 's'], // ѕ
	[0x04bb, 'h'], // һ
	[0x051b, 'q'], // ԛ
	[0x051d, 'w'], // ԝ
	[0x0410, 'A'], // А
	[0x0412, 'B'], // В
	[0x0415, 'E'], // Е
	[0x041d, 'H'], // Н
	[0x0406, 'I'], // І
	[0x0408, 'J'], // Ј
	[0x041a, 'K'], // К
	[0x041c, 'M'], // М
	[0x041e, 'O'], // О
	[0x0420, 'P'], // Р
	[0x0421, 'C'], // С
	[0x0405, 'S'], // Ѕ
	[0x0422, 'T'], // Т
	[0x0425, 'X'], // Х
	[0x04ae, 'Y'], // Ү
	[0x051a, 'Q'], // Ԛ
	[0x051c, 'W'], // Ԝ
];

/** The confusable table keyed by the Cyrillic character. It contains `HOMOGLYPHS` of `src/corpus/transforms/unicode.ts`. */
export const CONFUSABLES: Readonly<Record<string, string>> = Object.fromEntries(
	CONFUSABLE_PAIRS.map(([cp, latin]) => [String.fromCodePoint(cp), latin])
);

const CONF_BASE = 0x0400;
const CONF = new Uint8Array(0x0130);
for (const [cp, latin] of CONFUSABLE_PAIRS) CONF[cp - CONF_BASE] = latin.charCodeAt(0);

/** A code point as a regex escape (keeps invisible characters out of the source). */
function esc(cp: number): string {
	return `\\u${cp.toString(16).padStart(4, '0')}`;
}

/** One precompiled test: does the block hold an N7 character or a table letter at all? */
const SPECIAL = new RegExp(
	`[${[...N7_EVERYWHERE, ...N7_WORD_INTERNAL, ...CONFUSABLE_PAIRS.map(([cp]) => cp)].map(esc).join('')}]`
);
const N7_CLASS = [...N7_EVERYWHERE, ...N7_WORD_INTERNAL].map(esc).join('');
const HAS_N7 = new RegExp(`[${N7_CLASS}]`);
const N7_RUN = new RegExp(`[${N7_CLASS}]+`, 'g');
const HAS_ASCII_LETTER = /[A-Za-z]/;

/* ---------------- letter classes (cached per code point) ---------------- */

const K_NONE = 0;
const K_ASCII = 1;
const K_LATIN = 2; // a Latin-script letter beyond ASCII
const K_CYR_T = 3; // a Cyrillic letter in the confusable table
const K_CYR_N = 4; // any other Cyrillic letter
const K_OTHER = 5; // a letter of another script, Greek included
const K_MARK = 6;
const K_GREEK = 7; // Other for N8; a word letter for N7

const RE_L = /\p{L}/u;
const RE_M = /\p{M}/u;
const RE_LATIN = /\p{Script=Latin}/u;
const RE_CYR = /\p{Script=Cyrillic}/u;
const RE_GREEK = /\p{Script=Greek}/u;

function classify(cp: number): number {
	const s = String.fromCodePoint(cp);
	if (RE_M.test(s)) return K_MARK;
	if (!RE_L.test(s)) return K_NONE;
	if (RE_LATIN.test(s)) return K_LATIN;
	if (RE_CYR.test(s)) return cp >= CONF_BASE && CONF[cp - CONF_BASE] !== 0 ? K_CYR_T : K_CYR_N;
	if (RE_GREEK.test(s)) return K_GREEK;
	return K_OTHER;
}

const bmpClass = new Uint8Array(65536);
const bmpKnown = new Uint8Array(65536);
const astralClass = new Map<number, number>();

function classOfCp(cp: number): number {
	if (cp < 128) return (cp >= 65 && cp <= 90) || (cp >= 97 && cp <= 122) ? K_ASCII : K_NONE;
	if (cp > 0xffff) {
		let k = astralClass.get(cp);
		if (k === undefined) {
			k = classify(cp);
			astralClass.set(cp, k);
		}
		return k;
	}
	if (bmpKnown[cp] === 1) return bmpClass[cp] as number;
	const k = cp >= 0xd800 && cp <= 0xdfff ? K_NONE : classify(cp);
	bmpClass[cp] = k;
	bmpKnown[cp] = 1;
	return k;
}

/** Code point ending just before `i` (0 when none). */
function cpBefore(text: string, i: number): number {
	if (i <= 0) return 0;
	const lo = text.charCodeAt(i - 1);
	if (lo >= 0xdc00 && lo <= 0xdfff && i >= 2) {
		const hi = text.charCodeAt(i - 2);
		if (hi >= 0xd800 && hi <= 0xdbff) return ((hi - 0xd800) << 10) + (lo - 0xdc00) + 0x10000;
	}
	return lo;
}

/** Code point starting at `i` (0 when past the end). */
function cpAt(text: string, i: number): number {
	if (i >= text.length) return 0;
	const hi = text.charCodeAt(i);
	if (hi >= 0xd800 && hi <= 0xdbff && i + 1 < text.length) {
		const lo = text.charCodeAt(i + 1);
		if (lo >= 0xdc00 && lo <= 0xdfff) return ((hi - 0xd800) << 10) + (lo - 0xdc00) + 0x10000;
	}
	return hi;
}

/** N7 neighbour test: a Latin, Greek or Cyrillic letter, or a combining mark. */
function isWordSide(cp: number): boolean {
	if (cp === 0) return false;
	const k = classOfCp(cp);
	return k !== K_NONE && k !== K_OTHER;
}

/* ---------------- N7 ---------------- */

interface Removal {
	text: string;
	/** Old-text offsets of the removed characters, ascending. */
	removed: number[];
}

function removeInvisible(text: string): Removal | null {
	const removed: number[] = [];
	N7_RUN.lastIndex = 0;
	const n = text.length;
	for (let m = N7_RUN.exec(text); m !== null; m = N7_RUN.exec(text)) {
		const a = m.index;
		const b = a + m[0].length;
		const inside = a > 0 && b < n && isWordSide(cpBefore(text, a)) && isWordSide(cpAt(text, b));
		for (let k = a; k < b; k++) {
			const c = text.charCodeAt(k);
			if (inside || c === 0x200b || c === 0xfeff) removed.push(k);
		}
	}
	if (removed.length === 0) return null;
	const parts: string[] = [];
	let prev = 0;
	for (const p of removed) {
		if (p > prev) parts.push(text.slice(prev, p));
		prev = p + 1;
	}
	if (prev < n) parts.push(text.slice(prev));
	return { text: parts.join(''), removed };
}

/** The map after removals: the old pairs, plus one new pair after every removed character. */
function composeMap(
	map: readonly number[] | undefined,
	srcStart: number,
	removed: number[],
	n: number
): number[] {
	const pairs: number[] = [];
	const push = (t: number, s: number): void => {
		const k = pairs.length;
		if (k >= 2 && t - (pairs[k - 2] as number) === s - (pairs[k - 1] as number)) return;
		pairs.push(t, s);
	};
	let j = 0; // first old pair with offset > the current segment start
	let t = 0;
	let a = 0;
	const segment = (b: number): void => {
		if (b <= a) return;
		push(t, srcAt(map, srcStart, a));
		if (map) {
			while (j * 2 < map.length && (map[j * 2] as number) <= a) j++;
			while (j * 2 < map.length && (map[j * 2] as number) < b) {
				push(t + (map[j * 2] as number) - a, map[j * 2 + 1] as number);
				j++;
			}
		}
		t += b - a;
	};
	for (const p of removed) {
		segment(p);
		a = p + 1;
	}
	segment(n);
	return pairs;
}

/* ---------------- N8 ---------------- */

const R_OTHER = 0;
const R_LATIN = 1; // ASCII-Latin-only
const R_CYR = 2; // Cyrillic letters only, all in the table
const R_MIXED = 3; // ASCII Latin + table Cyrillic, nothing else (acts as Latin for its neighbours)
const R_MIXED_ACC = 4; // as R_MIXED with a non-ASCII Latin letter too (does not act as ASCII Latin)

const F_ASCII = 1;
const F_LATIN = 2;
const F_CYR_T = 4;
const F_BAD = 8; // a Cyrillic letter outside the table, or a letter of another script

function runKind(f: number): number {
	if (f === F_ASCII) return R_LATIN;
	if (f === F_CYR_T) return R_CYR;
	if ((f & F_BAD) === 0 && (f & F_ASCII) !== 0 && (f & F_CYR_T) !== 0)
		return (f & F_LATIN) === 0 ? R_MIXED : R_MIXED_ACC;
	return R_OTHER;
}

interface Mapped {
	text: string;
	/** Offsets of the mapped letters (ascending) and the Cyrillic code point each replaced. */
	at: number[];
	from: number[];
}

function mapLookalikes(text: string): Mapped | null {
	const n = text.length;
	const runS: number[] = [];
	const runE: number[] = [];
	const runK: number[] = [];
	let i = 0;
	while (i < n) {
		let c = text.charCodeAt(i);
		let cp = c;
		let w = 1;
		if (c >= 0xd800 && c <= 0xdbff && i + 1 < n) {
			cp = cpAt(text, i);
			w = cp > 0xffff ? 2 : 1;
		}
		let k = classOfCp(cp);
		if (k === K_NONE) {
			i += w;
			continue;
		}
		const start = i;
		let f = 0;
		while (k !== K_NONE) {
			if (k === K_ASCII) f |= F_ASCII;
			else if (k === K_LATIN) f |= F_LATIN;
			else if (k === K_CYR_T) f |= F_CYR_T;
			else if (k !== K_MARK) f |= F_BAD;
			i += w;
			if (i >= n) break;
			c = text.charCodeAt(i);
			cp = c;
			w = 1;
			if (c >= 0xd800 && c <= 0xdbff && i + 1 < n) {
				cp = cpAt(text, i);
				w = cp > 0xffff ? 2 : 1;
			}
			k = classOfCp(cp);
		}
		runS.push(start);
		runE.push(i);
		runK.push(runKind(f));
	}
	const nr = runK.length;
	const at: number[] = [];
	const from: number[] = [];
	const mapRun = (r: number): void => {
		for (let p = runS[r] as number; p < (runE[r] as number); p++) {
			const c = text.charCodeAt(p);
			if (c >= CONF_BASE && c - CONF_BASE < CONF.length && CONF[c - CONF_BASE] !== 0) {
				at.push(p);
				from.push(c);
			}
		}
	};
	const latinLike = (k: number): boolean => k === R_LATIN || k === R_MIXED;
	for (let r = 0; r < nr; r++) {
		const k = runK[r] as number;
		if (k === R_MIXED || k === R_MIXED_ACC) mapRun(r);
		else if (k === R_CYR) {
			const prevEdge = r === 0;
			const nextEdge = r === nr - 1;
			if (prevEdge && nextEdge) continue;
			if (!prevEdge && !latinLike(runK[r - 1] as number)) continue;
			if (!nextEdge && !latinLike(runK[r + 1] as number)) continue;
			mapRun(r);
		}
	}
	if (at.length === 0) return null;
	const parts: string[] = [];
	let prev = 0;
	for (let m = 0; m < at.length; m++) {
		const p = at[m] as number;
		parts.push(
			text.slice(prev, p),
			String.fromCharCode(CONF[(from[m] as number) - CONF_BASE] as number)
		);
		prev = p + 1;
	}
	parts.push(text.slice(prev));
	return { text: parts.join(''), at, from };
}

/* ---------------- the step ---------------- */

/** What `applyHygiene` adds up over the blocks of a document. */
export interface HygieneAcc {
	invisible: number;
	lookalikes: number;
	/** The first 5 events by position, offsets in the final `doc.text`. */
	first: HygieneEvent[];
}

export const FIRST_MAX = 5;

export function newHygieneAcc(): HygieneAcc {
	return { invisible: 0, lookalikes: 0, first: [] };
}

/**
 * N7 then N8 on one normalised block. `base` is the block's start in `doc.text`. Returns `nm` itself (no allocation)
 * when nothing changes.
 */
export function applyHygiene(
	nm: Normalised,
	srcStart: number,
	base: number,
	acc: HygieneAcc
): Normalised {
	let text = nm.text;
	if (!SPECIAL.test(text)) return nm;
	let map = nm.map;
	let toNew = nm.toNew;
	const events: HygieneEvent[] = [];
	let changed = false;

	if (HAS_N7.test(text)) {
		const rem = removeInvisible(text);
		if (rem) {
			const removed = rem.removed;
			acc.invisible += removed.length;
			if (acc.first.length < FIRST_MAX) {
				for (let k = 0; k < Math.min(FIRST_MAX, removed.length); k++) {
					const p = removed[k] as number;
					events.push({ offset: base + p - k, kind: 'invisible', codePoint: text.charCodeAt(p) });
				}
			}
			map = composeMap(nm.map, srcStart, removed, text.length);
			const prevToNew = nm.toNew;
			const dropped = (o: number): number => {
				if (o <= 0) return 0;
				let lo = 0;
				let hi = removed.length;
				while (lo < hi) {
					const mid = (lo + hi) >> 1;
					if ((removed[mid] as number) < o) lo = mid + 1;
					else hi = mid;
				}
				return o - lo;
			};
			toNew = prevToNew ? (o: number) => dropped(prevToNew(o)) : dropped;
			text = rem.text;
			changed = true;
		}
	}

	if (HAS_ASCII_LETTER.test(text)) {
		const m = mapLookalikes(text);
		if (m) {
			acc.lookalikes += m.at.length;
			if (acc.first.length < FIRST_MAX) {
				for (let k = 0; k < Math.min(FIRST_MAX, m.at.length); k++)
					events.push({
						offset: base + (m.at[k] as number),
						kind: 'lookalike',
						codePoint: m.from[k] as number,
					});
			}
			text = m.text;
			changed = true;
		}
	}

	if (events.length > 0) {
		events.sort((a, b) => a.offset - b.offset);
		for (const e of events) {
			if (acc.first.length >= FIRST_MAX) break;
			acc.first.push(e);
		}
	}
	return changed ? { text, map, toNew } : nm;
}
